import {useMemo} from 'react';

import PropTypes from 'prop-types';

import {useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {ProductPickerView} from '@amblique/king-living/app/components/complex-customize/product-picker-view';
import {StepsMainView} from '@amblique/king-living/app/components/complex-customize/steps-main-view';
import {resolveOptionSwatchThumbnailUrl} from '@amblique/king-living/app/components/configuration-selector/utils';
import {computeLiveListSubtotal, computeLiveSubtotal, joinStepSlotSelectionTitles} from '@amblique/king-living/app/components/complex-customize/utils/selection-utils';
import {findFirstIncompleteVisibleStep, getVisibleCustomizeSteps} from '@amblique/king-living/app/hooks/use-customize-steps';
import {useCustomizeJourney} from '@amblique/king-living/app/hooks/use-customize-journey';
import {useCustomizeStepsContent} from '@amblique/king-living/app/hooks/use-customize-steps-content';
import {buildFlyoutSelectionSummaryText} from '@amblique/king-living/app/utils/build-selection-summary-text';
import {formatOptionProductSelectionName} from '@amblique/king-living/app/utils/customize-selections';

/**
 * Shape expected by SubtotalFooter thumbnail + title.
 * @param {Object|null|undefined} sel
 * @returns {{imageUrl: string|null, name: string}|null}
 */
function selectionToFooterProduct(sel) {
  if (!sel) return null;

  return {imageUrl: sel.imageUrl ?? null, name: sel.name};
}

/** Picker preview → footer shape (not committed SlotCard). */
function previewProductToFooter(product) {
  const name = formatOptionProductSelectionName(product);

  if (!name) return null;

  return {
    imageUrl: resolveOptionSwatchThumbnailUrl(product),
    name,
  };
}

/**
 * Patch live picker preview into selections for the flyout footer summary.
 * Only writes a non-empty product/variant title so blank options are omitted
 * from the comma-joined line (e.g. "Leura Natural White, Powder Coat").
 *
 * @param {Object|null|undefined} selections
 * @param {{stepId: string, slotIndex: number}|null|undefined} activeSlot
 * @param {Object|null|undefined} galleryPreviewProduct
 * @returns {Object|null|undefined}
 */
function patchSelectionsForFooterSummary(selections, activeSlot, galleryPreviewProduct) {
  if (!selections || !activeSlot || !galleryPreviewProduct) return selections;

  // Cover commits immediately on pick — never overwrite draft cover from a stale preview.
  if (activeSlot.stepId === 'cover') {
    return selections;
  }

  const previewName = formatOptionProductSelectionName(galleryPreviewProduct);

  if (!previewName) return selections;

  const row = [...(selections?.[activeSlot.stepId] || [])];

  row[activeSlot.slotIndex] = {
    ...(row[activeSlot.slotIndex] ?? {}),
    imageUrl: resolveOptionSwatchThumbnailUrl(galleryPreviewProduct),
    name: previewName,
  };

  return {...selections, [activeSlot.stepId]: row};
}

function hasConfirmableOptionProduct(
  galleryPreviewProduct,
  selections,
  activeSlot,
  optionPreviewCleared
) {
  if (optionPreviewCleared) {
    return Boolean(selections?.[activeSlot?.stepId]?.[activeSlot?.slotIndex]?.productId);
  }

  const productId =
    galleryPreviewProduct?.productId ||
    galleryPreviewProduct?.id ||
    selections?.[activeSlot?.stepId]?.[activeSlot?.slotIndex]?.productId;

  if (!productId) return false;

  // Require a non-empty shopper label — id alone must not enable Next/Done.
  const previewName = formatOptionProductSelectionName(galleryPreviewProduct);
  if (previewName) return true;

  const selectedName = selections?.[activeSlot?.stepId]?.[activeSlot?.slotIndex]?.name;

  return typeof selectedName === 'string' && Boolean(selectedName.trim());
}

function optionPickerHighlightProductId(
  activeSlot,
  galleryPreviewProduct,
  selections,
  optionPreviewCleared
) {
  if (!activeSlot || activeSlot.stepId === 'cover') return null;

  if (optionPreviewCleared) return null;

  return (
    galleryPreviewProduct?.productId ??
    galleryPreviewProduct?.id ??
    selections?.[activeSlot.stepId]?.[activeSlot.slotIndex]?.productId ??
    null
  );
}

/**
 * Pick the selection to show in the subtotal footer for a completed step.
 * For slot-cards, uses the last visible slot that has a selection (not merely values.length - 1),
 * so hidden trailing slots or empty auto-picks do not override the user's last visible choice.
 *
 * @param {Object} step - Step from buildSteps()
 * @param {Object} selections - Full selections map
 * @returns {{imageUrl: string|null, name: string}|null}
 */
function getFooterProductForStep(step, selections) {
  if (!step) return null;
  if (step.id === 'cover') {
    return selectionToFooterProduct(selections?.cover);
  }
  if (step.layoutType === 'radio') {
    return selectionToFooterProduct(selections?.[step.id]?.[0]);
  }
  if (step.layoutType === 'slot-cards' && step.container?.values) {
    const title = joinStepSlotSelectionTitles(step, selections);

    if (!title) return null;

    const stepSelections = selections?.[step.id] || [];

    for (let i = step.container.values.length - 1; i >= 0; i--) {
      const slotMeta = step.container.values[i];

      if (slotMeta?.isVisible === false) continue;
      const sel = stepSelections[i];

      if (sel != null) {
        const thumb = selectionToFooterProduct(sel);

        return {imageUrl: thumb?.imageUrl ?? null, name: title};
      }
    }

    return {imageUrl: null, name: title};
  }

  return null;
}

/**
 * Customize steps panel content.
 * Single scrollable main screen with all steps inline; "Choose" on a slot opens a product picker sub-view.
 * All state and interaction logic lives in useCustomizeStepsContent.
 *
 * KINDW-1124 — `viewMode` + controlled active slot:
 *   - `viewMode='auto'` (default, Complex PDP): mutually-exclusive render — picker
 *     when activeSlot is set, otherwise StepsMainView. Existing behaviour, unchanged.
 *   - `viewMode='main'`: always render StepsMainView regardless of activeSlot.
 *     Used by the inline instance in `<CustomizeStepsInline>` so the page body keeps
 *     showing the steps list while the Drawer-hosted picker is open.
 *   - `viewMode='picker'`: render ProductPickerView when activeSlot is set, else null.
 *     Used by the Drawer instance in `<CustomizeStepsInline>` so the flyout shows
 *     only the current option's information (FSD §8.2 / PDP-07).
 *   `controlledActiveSlot` + `onActiveSlotChange` are forwarded to the hook so the
 *   two instances share active-slot state via the parent. See hook docs for details.
 */
export function CustomizeStepsContent({
  contentBodyStyles,
  controlledActiveSlot,
  coverGroups,
  coverVariantProduct,
  committedOptionProduct,
  drawerHeight,
  enableLocking = true,
  hasUserInteracted = false,
  isPredefinedSetView = false,
  isSimplePDP = false,
  onActiveSlotChange,
  onDone,
  onFlyoutStepEverComplete,
  onGalleryProductChange,
  onHeaderChange,
  onCoverPickerOpen,
  onSelect,
  onSlotProductPreview,
  onSubViewChange,
  onUserInteract,
  onViewDetails,
  product,
  selections,
  showSubtotalFooter = true,
  subtotalListPrice,
  subtotalPrice,
  viewMode = 'auto',
}) {
  const styles = useMultiStyleConfig('CustomizeSteps', {});
  const {flyoutDraftState} = useCustomizeJourney();
  const stepsEverCompleteIds = onFlyoutStepEverComplete ?
    (flyoutDraftState.stepsEverCompleteIds ?? []) :
    [];

  const {
    STEPS,
    activeSlot,
    activeStepData,
    activeStepId,
    bodyHeight,
    currency,
    galleryPreviewProduct,
    optionPreviewCleared,
    hasCoverData,
    isMobile,
    isStepComplete,
    liveMessage,
    masterMinPrice,
    scrollBodyRef,
    slotButtonRefs,
    stepRefs,
    handleCoverVariantViewDetails,
    handleMainNext,
    handleOptionProductViewDetails,
    handleSlotChoose,
    handleSlotNext,
    handleSlotProductPreview,
    handleStepInteraction,
  } = useCustomizeStepsContent({
    controlledActiveSlot,
    coverGroups,
    coverVariantProduct,
    committedOptionProduct,
    drawerHeight,
    hasUserInteracted,
    isSimplePDP,
    onActiveSlotChange,
    onFlyoutStepEverComplete,
    onGalleryProductChange,
    onHeaderChange,
    onCoverPickerOpen,
    onSelect,
    onSlotProductPreview,
    onSubViewChange,
    onUserInteract,
    onViewDetails,
    product,
    selections,
  });

  // While the user is browsing in a sub-panel, compute the subtotal in real-time
  // using the currently previewed product so the footer updates before "Next" is pressed.
  const liveSubtotalPrice = useMemo(
    () => computeLiveSubtotal(selections, activeSlot, galleryPreviewProduct, subtotalPrice),
    [selections, activeSlot, galleryPreviewProduct, subtotalPrice]
  );

  const liveSubtotalListPrice = useMemo(
    () => computeLiveListSubtotal(selections, activeSlot, galleryPreviewProduct),
    [selections, activeSlot, galleryPreviewProduct]
  );

  // Render if either cover data is available (complex PDP) or any option step has data (simple PDP).
  const hasOptionSteps = STEPS.filter((s) => s.id !== 'cover').some((s) => s.hasData());

  // Picker preview patch (option slots only). Joined summary is main-steps only — see below.
  // Keep above the early return so hook order stays stable.
  const selectionsForSummary = useMemo(
    () => patchSelectionsForFooterSummary(selections, activeSlot, galleryPreviewProduct),
    [selections, activeSlot, galleryPreviewProduct]
  );
  // Main complex view: join all selected product/variant titles (skip empty), incl. after Change.
  // Nested picker (cover / option): NO join — footer shows only the active step's name.
  const selectionSummary = !activeSlot ?
    buildFlyoutSelectionSummaryText(selectionsForSummary) :
    '';

  if (!hasCoverData && !hasOptionSteps) return null;

  const highlightedOptionProductId = optionPickerHighlightProductId(
    activeSlot,
    galleryPreviewProduct,
    selections,
    optionPreviewCleared
  );

  const getFooterProduct = () => {
    if (activeSlot) {
      if (activeSlot.stepId === 'cover') {
        const coverSel = selections?.cover;

        return {
          imageUrl: coverSel?.imageUrl ?? null,
          name: coverSel?.name ?? null,
          canConfirmSlot: Boolean(
            coverSel?.productId &&
              typeof coverSel?.name === 'string' &&
              coverSel.name.trim()
          ),
          emptyTitleContext: coverSel?.productId ? undefined : 'mainSteps',
        };
      }

      const previewFooter = optionPreviewCleared ?
        null :
        (previewProductToFooter(galleryPreviewProduct) ??
          selectionToFooterProduct(selections?.[activeSlot.stepId]?.[activeSlot.slotIndex]));
      const canConfirmSlot = hasConfirmableOptionProduct(
        galleryPreviewProduct,
        selections,
        activeSlot,
        optionPreviewCleared
      );

      if (activeStepData?.layoutType === 'slot-cards') {
        const product = getFooterProductForStep(activeStepData, selectionsForSummary);

        return {
          ...product,
          canConfirmSlot,
          emptyTitleContext: 'nestedSelection',
        };
      }

      // Radio / single-option picker: current option product name only.
      return {
        imageUrl: previewFooter?.imageUrl ?? null,
        name: previewFooter?.name ?? null,
        canConfirmSlot,
        emptyTitleContext: 'nestedSelection',
      };
    }

    // Main steps: joined summary when we have titles (set + after deviation).
    if (selectionSummary) {
      const visibleSteps = getVisibleCustomizeSteps(STEPS);
      const lastVisibleStep = visibleSteps[visibleSteps.length - 1];
      const lastStepProduct = getFooterProductForStep(lastVisibleStep, selections);

      return {
        imageUrl: selections?.cover?.imageUrl ?? lastStepProduct?.imageUrl ?? null,
        name: null,
      };
    }

    const visibleSteps = getVisibleCustomizeSteps(STEPS);
    const step = findFirstIncompleteVisibleStep(STEPS, isStepComplete) ?? visibleSteps[visibleSteps.length - 1];
    const product = getFooterProductForStep(step, selections);

    return (
      product ?? {
        emptyTitleContext: step?.id === 'cover' ? 'mainSteps' : 'nestedSelection',
      }
    );
  };

  const footerProduct = getFooterProduct();

  // KINDW-1124 — viewMode gating.
  // 'main'   → never render the picker (inline instance in CM flyout flow).
  // 'picker' → only render the picker; nothing when no active slot (Drawer instance).
  // 'auto'   → existing mutually-exclusive behaviour (Complex PDP and any other caller).
  const shouldRenderPicker =
    activeSlot && activeStepData && viewMode !== 'main';

  if (viewMode === 'picker' && !shouldRenderPicker) return null;

  if (shouldRenderPicker) {
    return (
      <ProductPickerView
        activeSlot={activeSlot}
        bodyHeight={bodyHeight}
        coverGroups={coverGroups}
        currency={currency}
        footerProduct={footerProduct}
        highlightedOptionProductId={highlightedOptionProductId}
        isMobile={isMobile}
        isSimplePDP={isSimplePDP}
        masterMinPrice={masterMinPrice}
        scrollBodyRef={scrollBodyRef}
        selectionSummary={selectionSummary}
        selections={selections}
        showSubtotalFooter={showSubtotalFooter}
        stepData={activeStepData}
        styles={styles}
        subtotalListPrice={liveSubtotalListPrice}
        subtotalPrice={liveSubtotalPrice}
        onSelect={onSelect}
        onSlotNext={handleSlotNext}
        onSlotProductPreview={handleSlotProductPreview}
        onViewDetails={
          activeSlot.stepId === 'cover' ? handleCoverVariantViewDetails : handleOptionProductViewDetails
        }
      />
    );
  }

  return (
    <StepsMainView
      activeStepId={activeStepId}
      bodyHeight={bodyHeight}
      contentBodyStyles={contentBodyStyles}
      coverGroups={coverGroups}
      currency={currency}
      enableLocking={enableLocking}
      footerProduct={footerProduct}
      galleryPreviewProduct={galleryPreviewProduct}
      hasUserInteracted={hasUserInteracted}
      isMobile={isMobile}
      isSimplePDP={isSimplePDP}
      isStepComplete={isStepComplete}
      liveMessage={liveMessage}
      product={product}
      scrollBodyRef={scrollBodyRef}
      selectionSummary={selectionSummary}
      selections={selections}
      showSubtotalFooter={showSubtotalFooter}
      slotButtonRefs={slotButtonRefs}
      stepRefs={stepRefs}
      steps={STEPS}
      stepsEverCompleteIds={stepsEverCompleteIds}
      styles={styles}
      subtotalListPrice={subtotalListPrice}
      subtotalPrice={subtotalPrice}
      onDone={onDone}
      onGalleryProductChange={onGalleryProductChange}
      onMainNext={handleMainNext}
      onSelect={onSelect}
      onSlotChoose={handleSlotChoose}
      onSlotProductPreview={handleSlotProductPreview}
      onStepInteraction={handleStepInteraction}
      onViewDetails={onViewDetails}
    />
  );
}

CustomizeStepsContent.displayName = 'CustomizeStepsContent';

CustomizeStepsContent.propTypes = {
  contentBodyStyles: PropTypes.object,
  controlledActiveSlot: PropTypes.shape({
    slotIndex: PropTypes.number.isRequired,
    stepId: PropTypes.string.isRequired,
  }),
  coverGroups: PropTypes.array.isRequired,
  coverVariantProduct: PropTypes.object,
  committedOptionProduct: PropTypes.object,
  drawerHeight: PropTypes.number,
  enableLocking: PropTypes.bool,
  hasUserInteracted: PropTypes.bool,
  isPredefinedSetView: PropTypes.bool,
  isSimplePDP: PropTypes.bool,
  onActiveSlotChange: PropTypes.func,
  onDone: PropTypes.func,
  onFlyoutStepEverComplete: PropTypes.func,
  onGalleryProductChange: PropTypes.func,
  onHeaderChange: PropTypes.func,
  onCoverPickerOpen: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  onSlotProductPreview: PropTypes.func,
  onSubViewChange: PropTypes.func,
  onUserInteract: PropTypes.func,
  onViewDetails: PropTypes.func,
  product: PropTypes.object.isRequired,
  selections: PropTypes.object,
  showSubtotalFooter: PropTypes.bool,
  subtotalListPrice: PropTypes.number,
  subtotalPrice: PropTypes.number,
  viewMode: PropTypes.oneOf(['auto', 'main', 'picker']),
};
